import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { buildIntercomTranscript } from "@redotech/redo-web/ai/chat/utils/messages";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { CSSProperties, ReactNode, useContext } from "react";
import { useIntercom } from "react-use-intercom";
import { useEscalationContext } from "./escalation-context";
import { useInChatEscalationFlag } from "./in-chat-escalation-flag-provider";
import { useInChatEscalation } from "./use-in-chat-escalation";

const buildIntercomMessage = (chatHistory: string) => {
  const currentUrl = window.location.href;
  return `We copied your conversation up to this point. Please press send to continue!\n\n---\n\n${chatHistory}\n\nLink: ${currentUrl}`;
};

const closeChatInterface = () => {
  const chatCloseButton = document.querySelector(
    '[data-testid="chat-close-button"]',
  );
  if (chatCloseButton instanceof HTMLElement) {
    chatCloseButton.click();
  }
};

const iconWrapperStyle: CSSProperties = {
  padding: "12px",
  borderRadius: "50%",
  backgroundColor: "var(--redo-colors-background-bg-brand-subtle)",
  border: "1px solid var(--redo-colors-border-border-brand)",
};

const buttonMinWidthStyle: CSSProperties = { minWidth: "120px" };

function EscalationCard({
  title,
  description,
  error,
  children,
}: {
  title: string;
  description: string;
  error: string | null;
  children: ReactNode;
}) {
  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      m="lg"
      p="lg"
      radius="md"
      shadow="lg"
    >
      <Flex align="center" dir="column" gap="sm">
        <div style={iconWrapperStyle}>
          <RedoIcon color="brand" Icon={MessageChatCircleIcon} size={20} />
        </div>

        <Text fontSize="md" fontWeight="semibold" textAlign="center">
          {title}
        </Text>

        <Text color="secondary" fontSize="sm" textAlign="center">
          {description}
        </Text>
      </Flex>

      {error && (
        <Text color="error" fontSize="xs" textAlign="center">
          {error}
        </Text>
      )}

      {children}
    </Flex>
  );
}

export function EscalateToHumanTool(props: ToolComponentProps) {
  const inChatEscalation = useInChatEscalationFlag();
  const { sendToolResponse, messages } = useContext(AiChatContext);
  const intercom = useIntercom();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { triageThreadId, navigateToThread, escalation } =
    useEscalationContext();

  const activeEscalationLoad = useLoad(
    async (signal) => {
      if (!inChatEscalation) {
        return null;
      }
      const result = await merchantRpcClient.checkActiveEscalation(
        {},
        { signal },
      );
      return result.activeEscalation;
    },
    [inChatEscalation, merchantRpcClient],
  );

  const activeEscalation = activeEscalationLoad.value;
  const hasExistingEscalationOnDifferentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId !== triageThreadId;
  const hasExistingEscalationOnCurrentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId === triageThreadId;

  const sendEscalationResponse = useHandler(() => {
    void sendToolResponse(
      {
        toolCallId: props.toolCallId,
        toolName: props.toolName,
        result: { output: "Conversation escalated to human support" },
      },
      { skipLlmCall: true },
    );
  });

  // Legacy behavior: Open Intercom popup
  const handleTalkToHuman = useHandler(() => {
    const chatHistory = buildIntercomTranscript(messages);
    const intercomMessage = buildIntercomMessage(chatHistory);

    try {
      intercom.showNewMessage(intercomMessage);
      sendEscalationResponse();
      closeChatInterface();
    } catch {
      sendEscalationResponse();
    }
  });

  // New behavior: In-chat escalation
  const { escalate, isLoading, error, isEscalated } = useInChatEscalation({
    messages,
    input: props.input,
    onSuccess: sendEscalationResponse,
  });

  const handleEscalation = inChatEscalation ? escalate : handleTalkToHuman;

  const handleSameIssue = useHandler(() => {
    if (activeEscalation && navigateToThread) {
      navigateToThread(activeEscalation.triageThreadId);
      void sendToolResponse(
        {
          toolCallId: props.toolCallId,
          toolName: props.toolName,
          result: { output: "Navigated to existing escalation" },
        },
        { skipLlmCall: true },
      );
    }
  });

  // Don't render anything until the active escalation check completes.
  // Only gate on pending — if the RPC errors, fall through to show the normal button.
  if (inChatEscalation && activeEscalationLoad.pending) {
    return null;
  }

  if (
    inChatEscalation &&
    (isEscalated ||
      props.output !== undefined ||
      props.disabled ||
      escalation.status !== "idle" ||
      hasExistingEscalationOnCurrentThread)
  ) {
    return null;
  }

  // Two-button UI when there's an existing active escalation on a different thread
  if (inChatEscalation && hasExistingEscalationOnDifferentThread) {
    return (
      <EscalationCard
        description="Would you like to continue your existing conversation?"
        error={error}
        title="You already have an open conversation with the team"
      >
        <Flex gap="md">
          <RedoButton
            disabled={props.disabled || !navigateToThread}
            hierarchy="primary"
            onClick={handleSameIssue}
            size="sm"
            style={buttonMinWidthStyle}
            text="Yes"
          />
          <RedoButton
            disabled={isLoading || props.disabled}
            hierarchy="secondary"
            onClick={handleEscalation}
            pending={isLoading}
            size="sm"
            style={buttonMinWidthStyle}
            text={isLoading ? "Connecting..." : "No"}
          />
        </Flex>
      </EscalationCard>
    );
  }

  return (
    <EscalationCard
      description="Our support team is here to help. We'll transfer your conversation and connect you with a specialist."
      error={error}
      title="Need human assistance?"
    >
      <RedoButton
        disabled={isLoading || props.disabled}
        hierarchy="primary"
        onClick={handleEscalation}
        pending={isLoading}
        size="sm"
        style={buttonMinWidthStyle}
        text={isLoading ? "Connecting..." : "Talk to Support"}
      />
    </EscalationCard>
  );
}
